import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useStaffMe, isMutating } from '../../hooks/useStaffMe';
import { useToast } from '../../components/ToastProvider';
import { FilterPills } from '../../components/FilterPills';
import { FilterSearch } from '../../components/FilterSearch';
import { ColumnTooltip, type ColumnTooltipItem } from '../../components/ColumnTooltip';
import {
  listAllCustomers,
  createCustomer,
  addCustomerUser,
  SUBSCRIPTION_STATES,
  SUBSCRIPTION_STATE_LABELS,
  MVP_TIERS,
  domainCountsLabel,
  shownPlan,
  type CustomerListItem,
  type SubscriptionState,
  type MvpTier,
} from '../../lib/customers-api';
import { ZoneCell, ZoneFilter, matchesZone } from '../../components/ZoneIndicators';
import { ResidencyCountryField, resolveCreateZone } from '../../components/ResidencyCountryField';
import { useZoneMap } from '../../lib/zone-map';
import { zoneFor } from '../../obw/residency';
import { useZoneReadReport } from '../../components/ZoneContext';
import { useWorkingZone } from '../../lib/zones';
import { listSubscribers, subscriberRoleLabel, type SubscriberListItem } from '../../lib/subscribers-api';
import { AddCreditModal } from './AddCreditModal';
import { DeletedAccountInvoices } from '../../components/DeletedAccountInvoices';
// #21 — the ONE mapper for staff-API failures. Never render err.message directly:
// it carries the status, the path and the response body.
import { friendlyFromUnknown } from '../../lib/friendly-error';
import { civilDayOf, currentTimeZone, formatDate, todayIn } from '../../lib/time-format';

/**
 * /admin/customers — Admin-tier customer management (Phase 5d-3b, ADR-039).
 *
 * Partners/Users pattern: header + create panel + tab strip + FilterSearch +
 * FilterPills (subscription state) + plan dropdown filter + sortable table.
 * Loads all customers client-side (MVP scale ~5-10 records) so filter counts
 * and sorts are accurate across the full set — same approach as User Management.
 *
 * Role gating: read_only excluded (PII per ADR-012). Create / overrides
 * limited to nexa_admin / super_admin.
 */
export function CustomersPage() {
  const me = useStaffMe();

  if (me.status === 'loading') {
    return <main className="portal-page"><p className="portal-muted">Loading…</p></main>;
  }
  if (me.status === 'error') {
    return (
      <main className="portal-page">
        <h1>Customers</h1>
        <div className="portal-error" role="alert">{me.message}</div>
      </main>
    );
  }

  return <CustomersSurface role={me.me.role} />;
}

// ---------------------------------------------------------------------------

/**
 * Subscription column-tooltip content (5d-3b UAT Fix 2). Trialing is included
 * even though the filter pill is deferred to 5d-4 (Stripe) — a customer can
 * still carry the state, and the help text should explain it.
 */
const SUBSCRIPTION_TOOLTIP: ColumnTooltipItem[] = [
  { term: 'Active', desc: 'Subscription is live and billing is current.' },
  { term: 'Past Due', desc: 'Payment overdue; access may be restricted.' },
  { term: 'Suspended', desc: 'Subscription suspended by staff action.' },
  { term: 'Cancelled', desc: 'Subscription terminated; account is closed.' },
  { term: 'Trialing', desc: 'On a complimentary trial period; no billing yet.' },
];

type TabId = 'all';
type SortKey = 'businessName' | 'plan' | 'subscriptionState' | 'createdAt';
type SortDir = 'asc' | 'desc';

/**
 * Whole continuous months subscribed, from createdAt to today (5d-3b D10).
 * Counts only fully-elapsed months — a customer created on the 20th is "0 mo"
 * until the 20th of the following month. Returns null for a missing/invalid date.
 */
export function monthsSubscribed(createdAt: string, now: Date = new Date(), zone: string = currentTimeZone()): number | null {
  if (!createdAt) return null;
  // Calendar days in the staff member's zone (lib/time-format.tsx), never the device's.
  const start = civilDayOf(createdAt, zone);
  if (!start) return null;
  const today = todayIn(zone, now);
  const [sy, sm, sd] = start.split('-').map(Number);
  const [ny, nm, nd] = today.split('-').map(Number);
  let months = (ny - sy) * 12 + (nm - sm);
  if (nd < sd) months -= 1;
  return Math.max(0, months);
}

function CustomersSurface({ role }: { role: string }) {
  const toast = useToast();
  const canMutate = isMutating(role as Parameters<typeof isMutating>[0]);

  const [items, setItems] = useState<CustomerListItem[]>([]);
  const [loadState, setLoadState] = useState<'loading' | 'loaded' | 'error'>('loading');
  const [loadError, setLoadError] = useState('');
  const [zoneWarning, setZoneWarning] = useState<string | null>(null);
  const [failedZones, setFailedZones] = useState<string[]>([]);
  /** ADR-120 — 'all' until an operator narrows to one jurisdiction. */
  const [zoneFilter, setZoneFilter] = useState<string>('all');
  // PL36 — the row-level Add-Credit action (opens AddCreditModal for one customer).
  const [creditFor, setCreditFor] = useState<CustomerListItem | null>(null);

  const [tab] = useState<TabId>('all');
  const [search, setSearch] = useState('');
  const [subStateFilter, setSubStateFilter] = useState<Set<SubscriptionState>>(
    new Set(SUBSCRIPTION_STATES),
  );
  const [planFilter, setPlanFilter] = useState('');
  // BOW-finesse B3 (30 Jun 2026) — beta accounts are now visible BY DEFAULT
  // (supersedes ADR-073 Am.2 A2.6's "hidden by default", which assumed beta
  // lived only on the onboarding dashboard). The old "Show beta" hide-gate is
  // now a normal include/only/hide filter; default 'all' shows everything.
  const [betaFilter, setBetaFilter] = useState<'all' | 'only' | 'hide'>('all');
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir }>({
    key: 'businessName',
    dir: 'asc',
  });

  const load = useCallback(async () => {
    setLoadState('loading');
    try {
      const { items: all, failedZones } = await listAllCustomers();
      setItems(all);
      setFailedZones(failedZones);
      setLoadState('loaded');
      // ADR-120 — a zone that did not answer contributes no rows, so a short list looks
      // authoritative. An operator searching for a customer must not be told, in effect, that
      // they do not exist.
      setZoneWarning(
        failedZones.length
          ? `${failedZones.join(' and ')} did not respond — customers in ${failedZones.length === 1 ? 'that zone are' : 'those zones are'} missing from this list.`
          : null,
      );
    } catch (err) {
      const msg = friendlyFromUnknown(err);
      setLoadError(msg);
      setLoadState('error');
      toast.push({ variant: 'error', title: 'Could not load customers', detail: msg });
    }
  }, [toast]);

  useEffect(() => { void load(); }, [load]);

  // ADR-123 Decision 1 — the navigation bar names the zones in this list and their counts.
  useZoneReadReport({ rows: loadState === 'loaded' ? items : null, failedZones, filter: zoneFilter });

  const subStateCounts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const s of SUBSCRIPTION_STATES) c[s] = 0;
    items.forEach((i) => { if (c[i.subscriptionState] !== undefined) c[i.subscriptionState]++; });
    return c;
  }, [items]);

  const subStatePills = useMemo(
    () => SUBSCRIPTION_STATES.map((s) => ({
      key: s,
      label: SUBSCRIPTION_STATE_LABELS[s],
      count: subStateCounts[s] ?? 0,
    })),
    [subStateCounts],
  );

  const plans = useMemo(
    () => Array.from(new Set(items.map((i) => shownPlan(i)).filter(Boolean))).sort(),
    [items],
  );

  const betaCount = useMemo(() => items.filter((i) => i.beta).length, [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = items.filter((c) => {
      // BOW-finesse B3 — beta accounts show by default; the Beta filter narrows.
      if (betaFilter === 'only' && !c.beta) return false;
      if (betaFilter === 'hide' && c.beta) return false;
      if (!subStateFilter.has(c.subscriptionState)) return false;
      if (planFilter && shownPlan(c) !== planFilter) return false;
      // ADR-120 — narrow to one jurisdiction. 'all' is the default so the merged view is
      // what an operator sees unless they ask otherwise.
      if (!matchesZone(c, zoneFilter)) return false;
      if (q) {
        const hay = `${c.businessName} ${c.email} ${c.customerNumber} ${c.consultant}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    const factor = sort.dir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      // The Plan column sorts on what it shows (the label, else the billed plan).
      const val = (c: CustomerListItem) => (sort.key === 'plan' ? shownPlan(c) : c[sort.key]);
      const av = val(a).toLowerCase?.() ?? val(a);
      const bv = val(b).toLowerCase?.() ?? val(b);
      return av < bv ? -1 * factor : av > bv ? 1 * factor : 0;
    });
  }, [items, search, subStateFilter, planFilter, betaFilter, zoneFilter, sort]);

  const toggleSort = (key: SortKey) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));
  const sortArrow = (key: SortKey) => (sort.key === key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ' ↕');

  return (
    <main className="portal-page portal-customers-page">
      <header className="portal-partners-header">
        <div>
          <h1>Customers</h1>
          <p className="portal-partners-subtitle">
            Subscriber account management — create, view, transition subscription state, change
            plan/tier, and apply credits. The single customer console (ADR-090).
          </p>
        </div>
      </header>

      {canMutate && <ManuallyAddCustomerPanel role={role} onCreated={() => void load()} />}

      {/* BOW-finesse B3 — the disabled "Trialing" tab (a Stripe-billing-trial
          stub, never wired) was removed. Beta trial is its own lifecycle concept
          (ADR-078), not a Stripe billing trial; a Stripe-trial tab gets added
          when/if that integration lands. */}
      <nav className="portal-partners-tabs" aria-label="Customers sections">
        <button
          type="button"
          className={`portal-partners-tab${tab === 'all' ? ' active' : ''}`}
          aria-current={tab === 'all' ? 'page' : undefined}
        >
          All Customers
        </button>
      </nav>

      <div className="portal-applications-controls">
        <FilterSearch
          value={search}
          onChange={setSearch}
          placeholder="Search name, email or CN…"
          ariaLabel="Search customers"
        />
        <FilterPills
          groups={subStatePills}
          selected={subStateFilter}
          onChange={(next) => setSubStateFilter(next as Set<SubscriptionState>)}
          ariaLabel="Filter by subscription state"
        />
      </div>

      <div className="portal-users-filters">
        {/* ADR-123 Decision 2 — always rendered; disabled with the reason in a one-zone bundle.
            Now the shared control rather than this page's own inline copy of it. */}
        <ZoneFilter value={zoneFilter} onChange={setZoneFilter} />
        <label className="portal-users-filter-label">
          Plan
          <select
            className="portal-select"
            value={planFilter}
            onChange={(e) => setPlanFilter(e.target.value)}
          >
            <option value="">All plans</option>
            {plans.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </label>
        <label className="portal-users-filter-label">
          Beta
          <select
            className="portal-select"
            value={betaFilter}
            onChange={(e) => setBetaFilter(e.target.value as 'all' | 'only' | 'hide')}
          >
            <option value="all">Include{betaCount > 0 ? ` (${betaCount})` : ''}</option>
            <option value="only">Beta only</option>
            <option value="hide">Hide beta</option>
          </select>
        </label>
      </div>

      {zoneWarning && (
        <div className="portal-error" role="alert">
          {zoneWarning}{' '}
          <button type="button" className="portal-link-button" onClick={() => void load()}>
            Retry
          </button>
        </div>
      )}
      {loadState === 'loading' && <p className="portal-muted">Loading customers…</p>}
      {loadState === 'error' && (
        <div className="portal-error" role="alert">
          Could not load customers. {loadError}{' '}
          <button type="button" className="portal-link-button" onClick={() => void load()}>
            Retry
          </button>
        </div>
      )}

      {loadState === 'loaded' && (
        <table className="portal-users-table portal-customers-table">
          <thead>
            <tr>
              <th scope="col">
                <button type="button" className="portal-users-sort-btn" onClick={() => toggleSort('businessName')}>
                  Business name{sortArrow('businessName')}
                </button>
              </th>
              <th scope="col">Zone</th>
              <th scope="col">Domains</th>
              <th scope="col">Email</th>
              <th scope="col">
                <button type="button" className="portal-users-sort-btn" onClick={() => toggleSort('plan')}>
                  Plan{sortArrow('plan')}
                </button>
              </th>
              <th scope="col" className="portal-customers-sub-col">
                <span className="portal-col-help-wrap">
                  <button type="button" className="portal-users-sort-btn" onClick={() => toggleSort('subscriptionState')}>
                    Subscription{sortArrow('subscriptionState')}
                  </button>
                  <ColumnTooltip columnName="Subscription" items={SUBSCRIPTION_TOOLTIP} />
                </span>
              </th>
              <th scope="col">
                <button type="button" className="portal-users-sort-btn" onClick={() => toggleSort('createdAt')}>
                  Created{sortArrow('createdAt')}
                </button>
              </th>
              <th scope="col">Months</th>
              {/* F6 Actions-column standard: visible header, icon button, wider
                  column — matching the icon-button Actions pattern on the portal
                  tables (applied per fork, §9 — no shared table component). */}
              <th scope="col" className="portal-actions-col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={9} className="portal-users-empty">
                  {subStateFilter.size === 0
                    ? 'No filter pills selected — click a subscription state pill to show customers.'
                    : 'No customers match the current filters.'}
                </td>
              </tr>
            )}
            {filtered.map((c) => (
              <tr key={c.accountId}>
                {/* `title` on every truncated cell — this table shares the capped
                    .portal-customers-table columns with the Assign surface, so a long
                    business name or email is ellipsised and needs a tooltip to stay
                    readable. */}
                <td>
                  <div className="portal-users-name" title={c.businessName}>
                    {c.businessName}
                    {/* POW/partner UAT punchlist item 7 (1 Jul 2026) — the
                        name-adjacent BETA pill is redundant: the Plan column
                        already shows "Beta" (BOW-finesse B3). Removed here; the
                        Plan cell + All/Only/Hide Beta filter stay. */}
                  </div>
                  <div className="portal-customers-cn-sub">
                    {c.customerNumber || '—'}
                    {c.beta && c.consultant ? ` · ${c.consultant}` : ''}
                  </div>
                </td>
                <td><ZoneCell zone={c.zone} /></td>
                <td className="portal-customers-domain" title={c.primaryDomain}>
                  {c.primaryDomain || '—'}
                  {/* D5 — how many domains, and their state; the primary alone hid four pending ones. */}
                  <div className="portal-customers-cn-sub portal-customers-domain-counts" data-testid="domain-counts">{domainCountsLabel(c.domainCounts)}</div>
                </td>
                <td className="portal-customers-email" title={c.email}>{c.email}</td>
                {/* BOW-finesse B3 — beta accounts have no subscription plan;
                    surface their plan as "Beta". Item 7 follow-up (1 Jul 2026):
                    render it as the standard purple beta pill (portal-beta-chip /
                    beta-chip-beta — the same chip the beta panel uses), not plain
                    text. Margin reset is scoped to the plan cell below. */}
                <td className="portal-customers-plan">
                  {c.beta
                    ? <span className="portal-beta-chip beta-chip-beta">Beta</span>
                    : (shownPlan(c) || '—')}
                </td>
                <td>
                  <span className={`portal-badge portal-badge-${c.subscriptionState}`}>
                    {SUBSCRIPTION_STATE_LABELS[c.subscriptionState]}
                  </span>
                </td>
                <td className="portal-users-created">
                  {c.createdAt ? formatDate(c.createdAt) : '—'}
                </td>
                <td className="portal-customers-months">
                  {(() => {
                    const m = monthsSubscribed(c.createdAt);
                    return m === null ? '—' : `${m} mo`;
                  })()}
                </td>
                <td className="portal-users-row-actions portal-actions-col">
                  {/* Measured-aligned icon row (34px buttons, 8px gap) — the
                      standard multi-action pattern (.portal-actions-row). */}
                  <div className="portal-actions-row">
                    {/* ⚠ THE SECOND LAYER, AND IT IS NOT REDUNDANT WITH THE LIST FILTER.
                        `listCustomers` already excludes operator orgs server-side (PR #440),
                        so on today's data this branch never fires. It is here because the
                        filter is one `attribute_not_exists` clause in one handler: relax it,
                        bypass it, or copy this table into a new screen that forgets it, and
                        the View button starts handing staff a route to the detail page's
                        destructive actions again — which is exactly how the 12 August 2026
                        near-miss happened. The row now carries its own marker, so the
                        actions stay hidden on the row's own evidence rather than on another
                        handler's filter continuing to hold. */}
                    {!c.operatorPortalType && (
                      <Link
                        to={`/admin/customers/${encodeURIComponent(c.accountId)}${c.zone ? `?zone=${c.zone}` : ''}`}
                        className="portal-action-icon-btn"
                        aria-label={`View ${c.businessName}`}
                        title={`View ${c.businessName}`}
                      >
                        {/* eye icon — the standard "view" action glyph */}
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      </Link>
                    )}
                    {/* PL36 — Add-Credit action icon (mutating roles only). Opens
                        the budget-enforced AddCreditModal for this customer. */}
                    {canMutate && !c.operatorPortalType && (
                      <button
                        type="button"
                        className="portal-action-icon-btn"
                        onClick={() => setCreditFor(c)}
                        aria-label={`Add credit to ${c.businessName}`}
                        title={`Add credit to ${c.businessName}`}
                      >
                        {/* dollar-sign glyph — the "add credit" action */}
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <line x1="12" y1="1" x2="12" y2="23" />
                          <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                        </svg>
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* PL36 — row-level Add-Credit modal (budget-enforced; friendly toast on
          the consultant hard-block). Also available on the customer detail. */}
      {creditFor && (
        <AddCreditModal
          accountId={creditFor.accountId}
          zone={creditFor.zone}
          businessName={creditFor.businessName}
          onClose={() => setCreditFor(null)}
          onApplied={() => { /* credits don't alter the list row; nothing to reload */ }}
        />
      )}

      {/* ADR-079 Amendment 1 — a deleted customer's kept invoices (read-only, 7 years). */}
      <DeletedAccountInvoices />
    </main>
  );
}

// ---------------------------------------------------------------------------

const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Manually Add Customer panel (5d-3b D3/D4/D6/D7/D8).
 *
 * Creates the same shared Account entity the SOW produces. Website/domain is
 * required (the primary-domain seed); email is optional for org accounts — the
 * owner is associated separately via the user-association flow (D5). super_admin
 * gets a UAT test-mode toggle (tags a throwaway account) and a Test Toasts
 * diagnostic, mirroring the Manual Add Partner panel (UatAssessField).
 */
function ManuallyAddCustomerPanel({ role, onCreated }: { role: string; onCreated: () => void }) {
  const toast = useToast();
  const isSuperAdmin = role === 'super_admin';
  const [businessName, setBusinessName] = useState('');
  const [website, setWebsite] = useState('');
  const [email, setEmail] = useState('');
  const [plan, setPlan] = useState<MvpTier>('NexaGrowth');
  const [uatTest, setUatTest] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  /**
   * ⭐ ADR-124 D5 — THE CUSTOMER'S COUNTRY DECIDES WHERE THIS ACCOUNT IS BORN. This panel used to
   * create in the home zone unconditionally, with nothing on screen saying so; the country now
   * derives the zone and the create is addressed there (`adminApiInZone`).
   */
  const [jurisdiction, setJurisdiction] = useState('');
  const zoneMap = useZoneMap();
  const workingZone = useWorkingZone();
  const derived = jurisdiction && zoneMap ? zoneFor(zoneMap, jurisdiction) : null;
  const createZone = resolveCreateZone(derived?.zone);
  // After a successful add: keep the new account so the operator can associate
  // a user without leaving the panel (5d-3b D5).
  const [created, setCreated] = useState<{ accountId: string; businessName: string; uatTest: boolean } | null>(null);
  const [assocOpen, setAssocOpen] = useState(false);

  const nameValid = businessName.trim().length > 0;
  const websiteValid = website.trim().length > 0;
  // Email is optional; only block submit when a non-empty value is malformed.
  const emailEntered = email.trim().length > 0;
  const emailOk = !emailEntered || EMAIL_RX.test(email.trim());
  const canSubmit = nameValid && websiteValid && emailOk && !!createZone;

  const submit = async () => {
    setBusy(true);
    setError(null);
    setEmailError(null);
    if (emailEntered && !EMAIL_RX.test(email.trim())) {
      setEmailError('That email doesn’t look right. Format: name@example.com');
      setBusy(false);
      return;
    }
    try {
      if (!createZone) return; // unreachable: the button is disabled without a derived zone
      const result = await createCustomer({
        businessName: businessName.trim(),
        website: website.trim(),
        email: emailEntered ? email.trim().toLowerCase() : undefined,
        plan,
        uatTest: uatTest && isSuperAdmin ? true : undefined,
        jurisdiction,
      }, createZone);
      toast.push({
        variant: 'success',
        title: `Customer added: ${result.businessName}`,
        detail: result.uatTest
          ? `CN: ${result.customerNumber} · UAT test account (tagged for cleanup).`
          : `CN: ${result.customerNumber} — open the record to associate a user.`,
      });
      setCreated({ accountId: result.accountId, businessName: result.businessName, uatTest: result.uatTest });
      setBusinessName('');
      setWebsite('');
      setEmail('');
      setPlan('NexaGrowth');
      setUatTest(false);
      setJurisdiction('');
      onCreated();
    } catch (err) {
      // #21 — the STATUS is matched on, never displayed; the fall-through is the
      // shared mapper's sentence, not the raw string it used to interpolate.
      const raw = err instanceof Error ? err.message : String(err);
      const friendly = raw.toLowerCase().includes('conflict') || raw.includes('409')
        ? 'An account with that email already exists.'
        : friendlyFromUnknown(err);
      setError(friendly);
      toast.push({ variant: 'error', title: 'Add customer failed', detail: friendly });
    } finally {
      setBusy(false);
    }
  };

  // Fires one of each toast variant — same diagnostic as UatAssessField.
  const fireTestToasts = () => {
    toast.push({ variant: 'info', title: 'Test toast — info', detail: 'Background context. Auto-dismisses after 6s.' });
    setTimeout(() => toast.push({ variant: 'success', title: 'Test toast — success', detail: 'Action completed. Auto-dismisses after 4s.' }), 400);
    setTimeout(() => toast.push({ variant: 'error', title: 'Test toast — error (HTTP 503)', detail: '/admin/customers\n{"message":"Service Unavailable"}' }), 800);
  };

  return (
    <section className="portal-uat-field portal-customers-create" aria-label="Manually Add Customer">
      <div className="portal-uat-field-header">
        <h2>Manually Add Customer</h2>
      </div>
      <p className="portal-uat-field-help">
        Manually create a subscriber account. Writes the same shared entity the Subscriber
        Onboarding Workflow (SOW) produces — a Customer Number (CN) is generated automatically.
        Website/domain is required; email is optional for org accounts. After creating, open the
        record to <strong>associate a user</strong>. To onboard them, run the{' '}
        <strong>Subscriber Onboarding (SOW)</strong> card on the dashboard.
      </p>

      {isSuperAdmin && (
        <label className="portal-uat-mode-toggle">
          <input
            type="checkbox"
            checked={uatTest}
            onChange={(e) => setUatTest(e.target.checked)}
            disabled={busy}
          />
          <span>
            UAT test mode <em>(super_admin)</em> — tags the account for cleanup and fakes
            the user-invite email (a known password is shown when you add the user)
          </span>
        </label>
      )}

      <div className="portal-uat-field-form">
        <label className="portal-uat-field-label">
          <span>Business Name</span>
          <input
            type="text"
            value={businessName}
            onChange={(e) => setBusinessName(e.target.value)}
            placeholder="Acme Pty Ltd"
            autoComplete="off"
          />
        </label>
        <label className="portal-uat-field-label">
          <span>Website / Domain</span>
          <input
            type="text"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            placeholder="acme.com.au or https://acme.com.au"
            autoComplete="off"
            spellCheck={false}
          />
        </label>
        {/* ADR-124 D5 — the customer's country; the zone follows from it. */}
        <ResidencyCountryField
          id="add-customer-country"
          value={jurisdiction}
          onChange={setJurisdiction}
          map={zoneMap}
          workingZone={workingZone}
          disabled={busy}
        />
        <label className="portal-uat-field-label">
          <span>Email <em className="portal-uat-field-hint">(optional — org accounts can omit)</em></span>
          <input
            type="email"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setEmailError(null); }}
            placeholder="owner@example.com"
            autoComplete="off"
          />
          {emailError && <span className="portal-uat-field-inline-error">{emailError}</span>}
        </label>
        <label className="portal-uat-field-label">
          <span>Plan / Tier</span>
          <select value={plan} onChange={(e) => setPlan(e.target.value as MvpTier)}>
            {MVP_TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>

        {created && (
          <div className="portal-users-uat-banner">
            <strong>{created.businessName} added.</strong>{' '}
            Now associate the account owner.{' '}
            <button
              type="button"
              className="portal-link-button"
              onClick={() => setAssocOpen(true)}
            >
              Associate user →
            </button>
          </div>
        )}
        {error && <div className="portal-error" role="alert">{error}</div>}

        <div className="portal-uat-field-actions">
          <button
            type="button"
            className="portal-btn"
            onClick={() => void submit()}
            disabled={busy || !canSubmit}
          >
            {busy ? 'Adding…' : uatTest && isSuperAdmin ? 'Add Test Customer' : 'Add Customer'}
          </button>
          <span className="portal-uat-field-cost">
            A Customer Number is generated. Open the record to associate a user; onboard them from
            the Subscriber Onboarding (SOW) card on the dashboard.
          </span>
          {isSuperAdmin && (
            <button
              type="button"
              className="portal-link-button-secondary portal-uat-toast-test"
              onClick={fireTestToasts}
              title="Fire one of each toast variant — confirms the notification UX"
            >
              🧪 Test Toasts
            </button>
          )}
        </div>
      </div>

      {assocOpen && created && (
        <AssociateUserModal
          accountId={created.accountId}
          businessName={created.businessName}
          uatAccount={created.uatTest}
          onClose={() => setAssocOpen(false)}
        />
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------

/**
 * Associate User modal (5d-3b D5) — search existing subscribers and associate,
 * or add a new owner inline (→ AdminCreateUser + activation email). No close
 * button / no [×] (D13); dismissed via the Done action.
 */
function AssociateUserModal({
  accountId,
  businessName,
  uatAccount,
  onClose,
}: {
  accountId: string;
  businessName: string;
  uatAccount: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<SubscriberListItem[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uatPassword, setUatPassword] = useState<string | null>(null);

  const newEmailValid = EMAIL_RX.test(newEmail.trim());

  const runSearch = async () => {
    setSearching(true);
    setError(null);
    try {
      setResults(await listSubscribers(search.trim() || undefined));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  const associate = async (cognitoSub: string, label: string) => {
    setBusy(true);
    setError(null);
    try {
      await addCustomerUser(accountId, { cognitoSub });
      toast.push({ variant: 'success', title: 'User associated', detail: `${label} is now linked to ${businessName}.` });
      onClose();
    } catch (err) {
      const raw = err instanceof Error ? err.message : String(err);
      const friendly = raw.includes('already_associated') ? 'That user is already associated with this account.' : friendlyFromUnknown(err);
      setError(friendly);
      toast.push({ variant: 'error', title: 'Could not associate user', detail: friendly });
    } finally {
      setBusy(false);
    }
  };

  const addNewUser = async () => {
    setBusy(true);
    setError(null);
    setUatPassword(null);
    try {
      const res = await addCustomerUser(accountId, {
        email: newEmail.trim().toLowerCase(),
        name: newName.trim() || undefined,
      });
      if (res.uatTest && res.uatTestPassword) {
        setUatPassword(res.uatTestPassword);
        toast.push({ variant: 'success', title: `Test owner created: ${res.email}`, detail: 'No email sent — known password shown.' });
      } else if (res.emailSent === false) {
        toast.push({
          variant: 'error',
          title: 'Owner created, but the activation email did NOT send',
          detail: `${res.email}: ${res.emailError ?? 'send failed'}. In SES sandbox only verified recipients receive mail.`,
        });
      } else {
        toast.push({ variant: 'success', title: `Owner added: ${res.email}`, detail: 'A subscriber activation email has been sent.' });
        onClose();
      }
    } catch (err) {
      const raw = err instanceof Error ? err.message : String(err);
      const friendly = raw.includes('user_already_exists')
        ? 'A subscriber with that email already exists — search for them above to associate instead.'
        : friendlyFromUnknown(err);
      setError(friendly);
      toast.push({ variant: 'error', title: 'Could not add user', detail: friendly });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="portal-modal-backdrop" role="presentation">
      <div className="portal-modal portal-users-drawer" role="dialog" aria-modal="true" aria-label="Associate user">
        <h2>Associate a user with {businessName}</h2>
        <p className="portal-muted">
          Search existing subscribers to link one, or add a new owner. New owners receive a
          subscriber activation email{uatAccount ? ' (faked in UAT — a known password is shown).' : '.'}
        </p>

        <section className="portal-users-actions">
          <h3>Find an existing subscriber</h3>
          <div className="portal-users-role-row">
            <input
              type="text"
              className="portal-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') void runSearch(); }}
              placeholder="Search email or customer number…"
              aria-label="Search subscribers"
            />
            <button type="button" className="portal-button-primary" disabled={searching} onClick={() => void runSearch()}>
              {searching ? 'Searching…' : 'Search'}
            </button>
          </div>

          {results !== null && (
            results.length === 0 ? (
              <p className="portal-muted">No matching subscribers — add a new owner below.</p>
            ) : (
              <ul className="portal-users-audit-list">
                {results.map((s) => (
                  <li key={s.cognitoSub || s.email}>
                    <span className="portal-users-audit-action">{s.email}</span>
                    <span className="portal-users-audit-detail"> · {subscriberRoleLabel(s.role)} · {s.customerNumber || s.subscriberId || '—'}</span>{' '}
                    <button
                      type="button"
                      className="portal-link-button"
                      disabled={busy}
                      onClick={() => void associate(s.cognitoSub, s.email)}
                    >
                      Associate
                    </button>
                  </li>
                ))}
              </ul>
            )
          )}

          <h3>Add new user</h3>
          <label className="portal-users-field">
            Name (optional)
            <input type="text" className="portal-input" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Jane Owner" />
          </label>
          <label className="portal-users-field">
            Email
            <input type="email" className="portal-input" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="owner@example.com" />
          </label>

          {uatPassword && (
            <div className="portal-users-uat-banner">
              <strong>Test owner created.</strong> Temporary password: <code>{uatPassword}</code> — sign in to complete onboarding.
            </div>
          )}
          {error && <div className="portal-error" role="alert">{error}</div>}
        </section>

        <div className="portal-modal-actions">
          <button type="button" className="portal-button-primary" disabled={busy || !newEmailValid} onClick={() => void addNewUser()}>
            {busy ? 'Working…' : 'Add New User'}
          </button>
          <button type="button" className="portal-link-button-secondary" onClick={onClose}>
            {uatPassword ? 'Done' : 'Close'}
          </button>
        </div>
      </div>
    </div>
  );
}
